import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SimpleAnimatedCounter from '../components/SimpleAnimatedCounter';

const Services = () => {
  useEffect(() => {
    document.title = 'Our Services - VReco Energy | Renewable Energy Solutions';
    
    // Initialize scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);
    
    const fadeElements = document.querySelectorAll('.fade-in');
    fadeElements.forEach(el => observer.observe(el));

    return () => {
      fadeElements.forEach(el => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="services-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>Our Services</h1>
          <p>Comprehensive renewable energy solutions for a sustainable future</p>
        </div>
      </section>

      {/* Core Services */}
      <section className="content-section">
        <div className="container">
          
          {/* Our Solutions Overview */}
          <div className="services-grid" style={{ marginBottom: '3rem' }}>
            <div className="service-card fade-in highlighted-service" style={{ textAlign: 'center' }}>
              <div className="service-icon highlighted-icon" style={{ margin: '0 auto 1.5rem' }}>
                <i className="fas fa-fan" style={{ fontSize: '2rem', color: 'white' }}></i>
              </div>
              <h3>Wind Energy Solutions</h3>
              <p style={{ textAlign: 'justify' }}>Comprehensive wind turbine generator (WTG) projects including site assessment, land acquisition, civil works, electrical infrastructure, and complete installation. From foundation construction to grid synchronization, we deliver end-to-end wind energy solutions with proven expertise in turbine erection and commissioning.</p>
            </div>
            <div className="service-card fade-in highlighted-service" style={{ textAlign: 'center' }}>
              <div className="service-icon highlighted-icon" style={{ margin: '0 auto 1.5rem' }}>☀️</div>
              <h3>Solar Power Systems</h3>
              <p style={{ textAlign: 'justify' }}>Complete solar farm development and photovoltaic installations tailored for utility-scale and commercial applications. Our solar solutions encompass site evaluation, panel mounting systems, inverter installations, electrical connections, and grid integration to maximize energy yield and ensure long-term performance.</p>
            </div>
            <div className="service-card fade-in highlighted-service" style={{ textAlign: 'center' }}>
              <div className="service-icon highlighted-icon" style={{ margin: '0 auto 1.5rem' }}>⚡</div>
              <h3>Hybrid Energy Systems</h3>
              <p style={{ textAlign: 'justify' }}>Innovative integrated renewable energy systems combining wind, solar, and energy storage technologies for optimal power generation. Our hybrid solutions provide enhanced grid stability, maximized energy output, and reduced intermittency through intelligent energy management and advanced control systems.</p>
            </div>
          </div>

          {/* 
          
          <h3 className="fade-in" style={{ marginBottom: '2rem', color: '#2c5530', fontSize: '1.8rem', textAlign: 'center' }}>Our Service Criterias</h3>
          
          */}

          <h2 className="section-title fade-in">Core Services</h2>
          <p className="section-subtitle fade-in">Integrated approach to renewable energy infrastructure development</p>
          
          <div className="services-grid">
            <div className="service-card fade-in" id="wtg">
              <div className="service-icon">🔧</div>
              <h3>WTG Projects</h3>
              <p>Designed to support the entire lifecycle of wind turbines, ensuring reliable performance and maximizing energy output. Our comprehensive WTG services include:</p>
              <ul style={{ marginTop: '1rem', color: '#666', lineHeight: '1.8' }}>
                <li>Site assessment and wind resource analysis</li>
                <li>Turbine selection and optimization</li>
                <li>Installation and commissioning</li>
                <li>Performance monitoring and maintenance</li>
                <li>Lifecycle management and upgrades</li>
              </ul>
            </div>
            
            <div className="service-card fade-in" id="mining">
              <div className="service-icon">⛏️</div>
              <h3>Mining (Bauxite & Limestone)</h3>
              <p>Selective, on-spec extraction with in-pit quality control, crushing/sizing, and compliant dispatch for bauxite and limestone supply chains.</p>
              <ul style={{ marginTop: '1rem', color: '#666', lineHeight: '1.8' }}>
                <li>Resource modeling, mine planning, and permitting</li>
                <li>Selective mining with sampling/portable XRF grade control</li>
                <li>Crushing, sizing, moisture management, and stockpiles</li>
                <li>Weighbridge-integrated dispatch and DGMS/MoEF alignment</li>
              </ul>
              <div style={{ marginTop: '1rem' }}>
                <Link to="/services/mining" className="cta-button" style={{ padding: '0.6rem 1.2rem', fontSize: '0.95rem' }}>View Mining Details</Link>
              </div>
            </div>
            
            <div className="service-card fade-in" id="equipment">
              <div className="service-icon">🚜</div>
              <h3>Heavy Equipment Rental</h3>
              <p>Efficiently rent our top-quality heavy equipment with streamlined service to optimize your project timelines:</p>
              <ul style={{ marginTop: '1rem', color: '#666', lineHeight: '1.8' }}>
                <li>Cranes and lifting equipment</li>
                <li>Excavators and earthmoving machinery</li>
                <li>Bulldozers and compactors</li>
                <li>Specialized renewable energy equipment</li>
                <li>Maintenance and operator services</li>
              </ul>
            </div>
            
            <div className="service-card fade-in" id="engineering">
              <div className="service-icon">📐</div>
              <h3>Design & Engineering</h3>
              <p>Custom engineering solutions tailored to meet specific energy needs and regulatory requirements:</p>
              <ul style={{ marginTop: '1rem', color: '#666', lineHeight: '1.8' }}>
                <li>Structural design and analysis</li>
                <li>Electrical system design</li>
                <li>Environmental impact assessments</li>
                <li>Regulatory compliance consulting</li>
                <li>Technical documentation and reporting</li>
              </ul>
            </div>
            
            <div className="service-card fade-in">
              <div className="service-icon">⚡</div>
              <h3>Electrical Infrastructure</h3>
              <p>Complete electrical infrastructure solutions for renewable energy projects:</p>
              <ul style={{ marginTop: '1rem', color: '#666', lineHeight: '1.8' }}>
                <li>33KV and 220KV electrical line networks</li>
                <li>DPDC electrical systems</li>
                <li>Substation design and construction</li>
                <li>Grid connection and synchronization</li>
                <li>Power quality and monitoring systems</li>
              </ul>
            </div>
            
            <div className="service-card fade-in">
              <div className="service-icon">🏗️</div>
              <h3>Civil & Infrastructure</h3>
              <p>Comprehensive civil works and infrastructure development services:</p>
              <ul style={{ marginTop: '1rem', color: '#666', lineHeight: '1.8' }}>
                <li>Land leveling and site preparation</li>
                <li>Wind turbine foundation construction</li>
                <li>Crane pad and hardstand construction</li>
                <li>Access road and pathway development</li>
                <li>Drainage and utility systems</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure Development Process */}
      <section className="section why-choose">
        <div className="container">
          <h2 className="section-title fade-in" style={{ color: 'white' }}>Infrastructure Development Process</h2>
          <p className="section-subtitle fade-in" style={{ color: 'rgba(255,255,255,0.9)' }}>Our systematic approach to successful project execution</p>
          
          <div className="features-grid">
            <div className="feature-item fade-in">
              <div className="feature-icon">🔍</div>
              <h3>1. Land Acquisition</h3>
              <p>Designed to ensure a smooth and efficient process to support successful wind energy installation with proper due diligence and legal compliance.</p>
            </div>
            
            <div className="feature-item fade-in">
              <div className="feature-icon">🏗️</div>
              <h3>2. Site Infrastructure Development</h3>
              <p>Comprehensive site preparation including access roads, foundations, and utility connections to support successful wind energy installations.</p>
            </div>
            
            <div className="feature-item fade-in">
              <div className="feature-icon">📦</div>
              <h3>3. Procurement & Logistics</h3>
              <p>Comprehensive approach to procurement, production & logistics ensures the efficient delivery of wind turbine generators and components.</p>
            </div>
            
            <div className="feature-item fade-in">
              <div className="feature-icon">⚙️</div>
              <h3>4. WTG Erection/Solar Module Installation</h3>
              <p>Wind turbine erection includes tower assembly, while solar installations encompass module mounting, with precision to meet project timelines and quality standards.</p>
            </div>
            
            <div className="feature-item fade-in">
              <div className="feature-icon">✅</div>
              <h3>5. Commissioning</h3>
              <p>Critical phase ensuring the turbine operates efficiently, safely & according to design specifications with comprehensive testing protocols.</p>
            </div>
            
            <div className="feature-item fade-in">
              <div className="feature-icon">🤝</div>
              <h3>6. Hand Over</h3>
              <p>Formal transfer of responsibility from construction & commissioning team to operations & maintenance team with complete documentation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Wind Turbine Generator Solutions */}
      <section className="content-section">
        <div className="container">
          <h2 className="section-title fade-in">Our Speiclization</h2>
          <p className="section-subtitle fade-in">Comprehensive WTG development process with detailed execution phases</p>
          
          
          
          {/* Detailed Process Sections */}
          <div className="services-grid">
            {/* Land Acquisition */}
            <div className="service-card fade-in">
              <div className="service-icon">🏞️</div>
              <h3>Land Acquisition</h3>
              <ul style={{ marginTop: '1rem', color: '#666', lineHeight: '1.8' }}>
                <li>Land identification and feasibility check</li>
                <li>TSR and land owner consent for approval and installation</li>
                <li>Lease Deed, Temporary and Permanent pathway land arrangement, Aerial rights</li>
                <li>Measurement sheet, Town Planning and N.A. application</li>
                <li>Village N.O.C.</li>
                <li>Boom and lattice assembly area arrangement</li>
                <li>RoW for civil work and Electrical line</li>
              </ul>
            </div>
            
            {/* Civil Work */}
            <div className="service-card fade-in">
              <div className="service-icon">🏗️</div>
              <h3>Civil Work</h3>
              <ul style={{ marginTop: '1rem', color: '#666', lineHeight: '1.8' }}>
                <li>Temporary and Permanent pathway development</li>
                <li>Area development work for WTG installation location</li>
                <li>Foundation excavation</li>
                <li>Crane pad, Boom assembly and Lattice assembly area development</li>
                <li>Foundation steel cage binding and RCC, PCC work</li>
                <li>Aggregate material testing and supply</li>
                <li>Documentation and Handover</li>
              </ul>
            </div>
            
            {/* Electrical Work */}
            <div className="service-card fade-in">
              <div className="service-icon">⚡</div>
              <h3>Electrical Work</h3>
              <ul style={{ marginTop: '1rem', color: '#666', lineHeight: '1.8' }}>
                <li>Route survey and site identification</li>
                <li>Pole erection, alignment and finishing</li>
                <li>Pole marking, accessories fitting</li>
                <li>Line testing and commissioning</li>
                <li>Documentation and Handover</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Specialized Energy Solutions */}
      <section className="content-section">
        <div className="container">
          <h2 className="section-title fade-in">Specialized Energy Solutions</h2>
          <p className="section-subtitle fade-in">Tailored solutions for diverse renewable energy needs</p>
          
          <div className="services-grid">
            <div className="service-card fade-in">
              <div className="service-icon">☀️</div>
              <h3>Solar Farm Development</h3>
              <p style={{ marginTop: '1rem' }}>Complete solar power plant development from site selection to grid connection, maximizing energy yield and return on investment.</p>
            </div>
            {/*
            <div className="service-card fade-in">
              <div className="service-icon">💧</div>
              <h3>Hydropower Solutions</h3>
              <p style={{ marginTop: '1rem' }}>Small to medium-scale hydropower development including feasibility studies, design, and implementation for sustainable water-based energy.</p>
            </div>
             */}
            <div className="service-card fade-in">
              <div className="service-icon">
                <i className="fas fa-fan" style={{ fontSize: '2rem', color: 'white' }}></i>
              </div>
              <h3>WTG Solutions</h3>
              <p style={{ marginTop: '1rem' }}>comprehensive wind turbine generator services including site assessment, turbine selection, installation, and maintenance for optimal wind energy harvesting and grid integration.</p>
            </div>
            
            <div className="service-card fade-in">
              <div className="service-icon">🏢</div>
              <h3>Energy-Efficient Buildings</h3>
              <p style={{ marginTop: '1rem' }}>Green building design and energy management systems to optimize energy consumption and incorporate renewable energy sources.</p>
            </div>
            
            <div className="service-card fade-in">
              <div className="service-icon">🌾</div>
              <h3>Biomass Power Plants</h3>
              <p style={{ marginTop: '1rem' }}>Biomass energy solutions utilizing agricultural waste and organic materials for sustainable power generation and waste management.</p>
            </div>
            
            <div className="service-card fade-in">
              <div className="service-icon">⚡</div>
              <h3>Hybrid Energy Systems</h3>
              <p style={{ marginTop: '1rem' }}>Integrated renewable energy systems combining wind, solar, and storage technologies for optimal power generation and grid stability.</p>
            </div>
            
            <div className="service-card fade-in">
              <div className="service-icon">🔋</div>
              <h3>Energy Storage Solutions</h3>
              <p style={{ marginTop: '1rem' }}>Battery storage systems and energy management solutions to enhance grid stability and optimize renewable energy utilization.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas & Expertise */}
      <section className="section">
        <div className="container">
          <h2 className="section-title fade-in">Service Areas & Expertise</h2>
          <p className="section-subtitle fade-in">Comprehensive coverage across Gujarat and expanding markets</p>
          
          <div className="about-content">
            <div className="about-text fade-in">
              <h3>Key Regions and Markets</h3>
              <p style={{ textAlign: 'justify' }}>Based in Gujarat, we serve clients across India with a focus on renewable energy development in high-potential regions. Our expertise spans various geographical and climatic conditions, ensuring optimal project design and execution.</p>
              
              <h3>Technical Capabilities</h3>
              <p style={{ textAlign: 'justify' }}>Our team combines deep technical knowledge with practical field experience. We stay current with the latest industry standards, regulations, and technological advances to deliver cutting-edge solutions.</p>
              
              <h3>Quality Assurance</h3>
              <p style={{ textAlign: 'justify' }}>Every project undergoes rigorous quality control processes, from initial design to final commissioning. We maintain the highest standards of safety, efficiency, and environmental compliance.</p>
            </div>
            
            <div className="stats fade-in">
              <div className="stat-item">
                <SimpleAnimatedCounter end={15} suffix="+" />
                <span className="stat-label">Service Areas</span>
              </div>
              <div className="stat-item">
                <SimpleAnimatedCounter end={50} suffix="+" />
                <span className="stat-label">Technical Experts</span>
              </div>
              <div className="stat-item">
                <SimpleAnimatedCounter end={365} />
                <span className="stat-label">Days Support</span>
              </div>
              <div className="stat-item">
                <SimpleAnimatedCounter end={100} suffix="%" />
                <span className="stat-label">Compliance</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section why-choose">
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
            <h2 style={{ color: 'white', marginBottom: '1rem' }}>Ready to Start Your Project?</h2>
            <p style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '2rem', fontSize: '1.1rem' }}>Let's discuss how our comprehensive services can bring your renewable energy vision to life.</p>
            <a href="/contact" className="cta-button" style={{ fontSize: '1.2rem', padding: '1.2rem 3rem' }}>Get Started Today</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;